import { useCallback, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import pluralize from 'pluralize';
import { useApolloClient } from '@apollo/client';

import ErrorMessage from 'approot/shared/error-message/error-message';
import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalActions,
  SimpleModalContentInner,
  SimpleModalContentInset,
  SimpleModalContentOuter,
  SimpleModalCardItem,
} from 'lib/modal/simple-modal-scrollable.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import type { InteractiveLessonSectionOverviewFragment } from '../lesson-builder.graphql.generated';
import { getSectionsAndSubSectionsOverview } from 'approot/activity-builder/activity-builder.utils';
import SimpleCardLabel from 'approot/shared/simple-card/simple-card-label';
import useDownloadPages from 'approot/shared/download-pages/download-pages';
import {
  ACTIVITY_BUILDER_SLIDE_RATIO,
  SectionOrSubSectionOverview,
} from 'approot/activity-builder/activity-builder.constants';
import {
  ActivitiesContentType,
  INTRODUCTION_SECTION_ID,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import CheckableCard from 'approot/shared/worksheet-card/checkbox-worksheet-card/checkable-card';
import {
  ActivityPagePreview,
  ActivityBuilderIntroPreview,
  ActivityBuilderPagePreview,
} from 'approot/activity-builder/page/activity-builder-page-preview';
import ActivityBuilderAnswersPagePreview from 'approot/activity-builder/page/activity-builder-answers-page-preview';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { getSmallestImage } from 'approot/activity-builder/components/shared/logic/get-smallest-image';
import DownloadActivitiesLanguageOnDemand from 'approot/shared/download-pages/download-activities-language-on-demand';
import { ActivityBuilderThemeContextProvider } from 'approot/activity-builder/theme/activity-builder-theme';
import { Heading20, SmallStyled } from 'approot/shared/styles/typography.style';
import CheckboxInput from 'lib/form/checkbox-input/checkbox-input';
import { pushDownloadActivitiesEvent } from 'approot/shared/download-pages/download-activities.utils';
import { PublishedLessonActivities } from '../graphql/get-published-lesson-activities.constants';
import { PublishedAssessmentActivities } from '../graphql/get-published-assessment-activities.constants';
import ActivityAnswersPrint from '../print/answers/activity-answers-print';
import { SECTION_PRINT_CONTAINER_CLASS_NAME } from '../print/answers/activity-answers-print.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useActivityAnswersPrintContext } from '../print/answers/activity-answers-print.context';
import { useTeacherGetAnswers } from '../present/activity-answers.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { getPrintMetadata } from '../print/answers/logic/get-print-metadata';
import ActivityAnswersPrintIntro from '../print/answers/activity-answers-print-intro';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import { updatePrintAssetsInCache } from '../logic/update-print-assets-in-cache.apollo';
import { hasAnswers } from '../components/shared/logic/has-answer';
import useUserData from 'approot/shared/user/user-data';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from '../activity-builder-draft.constants';
import { useGetActivityMetadata } from '../logic/use-get-activity-details';

const PAGES_FIELD = 'pages';

type Props = {
  activities:
    | PublishedLessonActivities
    | PublishedAssessmentActivities
    | DraftLessonActivities
    | DraftAssessmentActivities;
  authorId?: number | null;
  responsiveImage?: ResponsiveImage[] | null;
  sectionOverviews: InteractiveLessonSectionOverviewFragment[];
  version: string | null;
  fileName: string;
  dataEventTargetType: string;
  contentId: number;
  contentType: ActivitiesContentType;
  language: LanguageCodes;
  handleClose: () => void;
};

const TeacherDownloadActivities = ({
  activities,
  authorId,
  responsiveImage,
  sectionOverviews,
  fileName,
  version,
  contentId,
  contentType,
  dataEventTargetType,
  language = LANGUAGE_CODE_EN_AU,
  handleClose,
}: Props) => {
  const client = useApolloClient();

  const [includeResponses, setIncludeResponses] = useState<boolean>(false);
  const [isDownloadingAnswers, setIsDownloadingAnswers] =
    useState<boolean>(false);
  const [isGeneratingDownload, setIsGeneratingDownload] =
    useState<boolean>(false);

  const { user } = useUserData();
  const currentClass = useCurrentClassVar();
  const { showSuccess } = useSnackbar();
  const { setAnswers } = useActivityAnswersPrintContext();

  const { data: teacherAnswersData } = useTeacherGetAnswers({
    fetchPolicy: 'cache-and-network',
    onCompleted: res => {
      setAnswers(res.getMyAnswers);
    },
  });

  const canShowModelledResponses = useMemo(
    () => hasAnswers(teacherAnswersData?.getMyAnswers),
    [teacherAnswersData]
  );

  const focusQuestion =
    activities.__typename === 'LessonActivities'
      ? activities?.focusQuestion
      : null;
  const phenomena =
    activities.__typename === 'LessonActivities' ? activities?.phenomena : null;

  const assetKeys = useMemo(
    () => activities.assetKeys?.print || [],
    [activities]
  );

  const sortedSectionsOverview = useMemo(
    () => sortBy(sectionOverviews, 'sort'),
    [sectionOverviews]
  );

  const sectionsAndSubSectionsOverview = useMemo(
    () => getSectionsAndSubSectionsOverview(sortedSectionsOverview),
    [sortedSectionsOverview]
  );

  const allSelectedValue = useMemo(
    () => [
      INTRODUCTION_SECTION_ID,
      ...sectionsAndSubSectionsOverview.map(s => s.id),
    ],
    [sectionsAndSubSectionsOverview]
  );

  const { getMetaData } = useGetActivityMetadata({
    sectionsOverview: sortedSectionsOverview,
    contentType,
  });

  const printMetaData = useMemo(
    () =>
      getPrintMetadata({
        contentType,
        lesson:
          activities.__typename === 'LessonActivities'
            ? {
                name: activities.lesson.name,
                focusQuestion,
                phenomena,
                unit: activities.lesson.unit,
                subjects: activities.lesson.subjects,
              }
            : null,
        assessment:
          activities.__typename === 'AssessmentActivities'
            ? {
                name: activities.assessment.name,
                unit: activities.assessment.unit,
                subjects: activities.assessment.subjects,
              }
            : null,
        landscapeImageUrls: activities.landscapeImage?.responsive || [],
        className: currentClass?.name || '',
      }),
    [activities, contentType, focusQuestion, phenomena, currentClass]
  );

  const [selectedPages, setSelectedPages] =
    useState<string[]>(allSelectedValue);

  const totalPages = sectionsAndSubSectionsOverview.length + 1;

  const handleSelectAll = useCallback(() => {
    setSelectedPages(allSelectedValue);
  }, [allSelectedValue, setSelectedPages]);

  const handleDeselectAll = useCallback(() => {
    setSelectedPages([]);
  }, [setSelectedPages]);

  const {
    isLoading: isLoadingDownloadPages,
    hasError,
    handleDownloadPages,
  } = useDownloadPages();

  const handleCloseModal = useCallback(() => {
    setAnswers([]);
    handleClose();
  }, [setAnswers, handleClose]);

  const disableDownload = useMemo(() => {
    return (
      isLoadingDownloadPages ||
      isDownloadingAnswers ||
      isGeneratingDownload ||
      !selectedPages ||
      selectedPages.length === 0 ||
      (selectedPages.length === 1 &&
        selectedPages[0] === INTRODUCTION_SECTION_ID)
    );
  }, [
    isLoadingDownloadPages,
    isDownloadingAnswers,
    isGeneratingDownload,
    selectedPages,
  ]);

  const ratio = ACTIVITY_BUILDER_SLIDE_RATIO * 100;

  const handleDownloadDocument = useCallback(
    (
      assetKeys: {
        key: string;
        language: LanguageCodes;
        publicToken: string | null;
      }[]
    ) => {
      if (isLoadingDownloadPages) return;

      const assetKey = assetKeys.find(key => key.language === language)?.key;

      // when there is no key it means we have not yet generated a PDF download
      // for the selected language
      if (!assetKey) {
        setIsGeneratingDownload(true);
        return;
      }

      const sameAsOriginal = selectedPages.length === totalPages;

      const pagesToDownload = selectedPages.map(pageSectionId =>
        pageSectionId === INTRODUCTION_SECTION_ID
          ? 1
          : sectionsAndSubSectionsOverview.findIndex(
              s => s.id === pageSectionId
            ) + 2
      );

      pushDownloadActivitiesEvent({
        targetType: dataEventTargetType,
        targetIdentifier: contentId,
        pageNumbers: sortBy(pagesToDownload),
        isPagesFiltered: !sameAsOriginal,
        version,
        language,
      });

      handleDownloadPages({
        fileName: `${fileName}${
          language !== LANGUAGE_CODE_EN_AU ? `-${language}` : ''
        }`,
        pages: pagesToDownload,
        assetKey,
        sameAsOriginal,
        language,
      }).then(didDownload => {
        if (didDownload) {
          handleCloseModal();
        }
      });
    },
    [
      totalPages,
      isLoadingDownloadPages,
      selectedPages,
      sectionsAndSubSectionsOverview,
      language,
      version,
      fileName,
      dataEventTargetType,
      contentId,
      handleCloseModal,
      handleDownloadPages,
      setIsGeneratingDownload,
    ]
  );

  const handleDownload = useCallback(() => {
    if (includeResponses) {
      // generate an answer filled pdf
      setIsDownloadingAnswers(true);
    } else {
      // download using published pdf
      handleDownloadDocument(assetKeys);
    }
  }, [includeResponses, assetKeys, handleDownloadDocument]);

  const getTitleDetails = useCallback(
    (section: SectionOrSubSectionOverview) => {
      const { title, description, differentiation } = getMetaData(section);

      return {
        title,
        description,
        differentiation,
      };
    },
    [getMetaData]
  );

  const handleSaveAs = useCallback(async () => {
    const generatePdfWithImages =
      await import('approot/activity-builder/print/image-pdf-generator/generate-pdf-with-images');
    await generatePdfWithImages.default({
      htmlElementSelector: SECTION_PRINT_CONTAINER_CLASS_NAME,
      fileName: formatToLowerKebabCase(
        `${
          activities.__typename === 'LessonActivities'
            ? activities.lesson.name
            : activities.assessment.name
        }-${language}.pdf`
      ),
    });
    // TODO push data tracker event
    showSuccess('Your Activities have been downloaded');
    handleCloseModal();
  }, [language, activities, showSuccess, handleCloseModal]);

  return (
    <SimpleModal
      isVisible
      scrollWithinModal
      size={900}
      isOverflowable
      title="Choose which Activities to Download"
      headerTitle="Choose which Activities to Download"
      dataTrackingLabel="download-activities"
      onClose={handleCloseModal}
    >
      <SimpleModalContentOuter>
        <SimpleModalContentInner>
          <SimpleModalContentInset>
            {isDownloadingAnswers &&
              includeResponses &&
              version &&
              currentClass && (
                <ActivityAnswersPrint
                  version={version}
                  contentFontSize={activities?.styles?.fontSize}
                  metadata={{
                    ...printMetaData,
                    userName: user?.displayName || user?.email || '',
                  }}
                  contentId={contentId}
                  classId={currentClass.id}
                  language={language}
                  sectionOverviews={sortedSectionsOverview}
                  selectedSectionIds={selectedPages}
                  onRenderComplete={handleSaveAs}
                />
              )}

            {isGeneratingDownload && version && currentClass ? (
              <DownloadActivitiesLanguageOnDemand
                isStudentDownload={false}
                contentId={contentId}
                contentType={contentType}
                classId={currentClass.id}
                language={language}
                version={version}
                onComplete={newAssetKeys => {
                  if (newAssetKeys) {
                    handleDownloadDocument(newAssetKeys);
                    updatePrintAssetsInCache({
                      client,
                      contentId,
                      contentType,
                      version,
                      language,
                      assetKeys: [
                        ...assetKeys,
                        { __typename: 'AssetKey', ...newAssetKeys[0] },
                      ],
                    });
                  } else {
                    handleCloseModal();
                  }
                }}
              />
            ) : (
              <>
                {
                  <ActivityBuilderThemeContextProvider
                    initialFontSize={activities?.styles?.fontSize}
                  >
                    <Box
                      $alignItems="center"
                      $justifyContent="space-between"
                      $marginBottom={16}
                    >
                      <Heading20 marginBottom={0}>Activities</Heading20>
                      <SecondaryButton
                        type="button"
                        aria-label={`${
                          selectedPages.length === totalPages
                            ? 'Deselect'
                            : 'Select'
                        } all Teaching Notes`}
                        onClick={() => {
                          if (selectedPages.length === totalPages) {
                            handleDeselectAll();
                          } else {
                            handleSelectAll();
                          }
                        }}
                      >
                        {selectedPages.length === totalPages
                          ? 'Deselect All'
                          : 'Select All'}
                      </SecondaryButton>
                    </Box>
                    <BoxList $gap={16} role="group">
                      <SimpleModalCardItem>
                        <CheckableCard
                          content={
                            includeResponses ? (
                              <ActivityPagePreview>
                                <ActivityAnswersPrintIntro {...printMetaData} />
                              </ActivityPagePreview>
                            ) : (
                              <ActivityBuilderIntroPreview
                                focusQuestion={focusQuestion || null}
                                phenomena={phenomena || null}
                                assessmentName={
                                  activities.__typename ===
                                  'AssessmentActivities'
                                    ? activities?.assessmentName
                                    : null
                                }
                                authorId={authorId || undefined}
                                language={language}
                                introBackground={getSmallestImage(
                                  responsiveImage || null
                                )}
                                headerFooterContent={{
                                  unitName: printMetaData.unitName,
                                  subjectName: printMetaData.subjectName,
                                  contentName: printMetaData.contentName,
                                  pageNumber: 1,
                                }}
                              />
                            )
                          }
                          title="Introduction"
                          ratio={ratio}
                          fieldName={PAGES_FIELD}
                          value={INTRODUCTION_SECTION_ID}
                          id={INTRODUCTION_SECTION_ID}
                          checked={selectedPages.includes(
                            INTRODUCTION_SECTION_ID
                          )}
                          onChange={() => {
                            if (
                              selectedPages.includes(INTRODUCTION_SECTION_ID)
                            ) {
                              setSelectedPages(
                                selectedPages.filter(
                                  page => page !== INTRODUCTION_SECTION_ID
                                )
                              );
                            } else {
                              setSelectedPages([
                                ...selectedPages,
                                INTRODUCTION_SECTION_ID,
                              ]);
                            }
                          }}
                        />
                      </SimpleModalCardItem>
                      {sectionsAndSubSectionsOverview?.map((page, index) => {
                        const details = getTitleDetails(page);
                        return (
                          <SimpleModalCardItem key={page.id}>
                            <CheckableCard
                              content={
                                includeResponses && currentClass ? (
                                  <ActivityBuilderAnswersPagePreview
                                    id={page.id}
                                    key={`${page.id}-${language}`}
                                    contentId={contentId}
                                    contentType={contentType}
                                    classId={currentClass.id}
                                    sectionOverviews={sortedSectionsOverview}
                                    version={version}
                                    parentElement={null}
                                    language={language}
                                    headerFooterContent={{
                                      unitName: printMetaData.unitName,
                                      subjectName: printMetaData.subjectName,
                                      contentName: printMetaData.contentName,
                                      pageNumber: index + 1,
                                      userName:
                                        user?.displayName || user?.email || '',
                                    }}
                                  />
                                ) : (
                                  <ActivityBuilderPagePreview
                                    id={page.id}
                                    key={`${page.id}-${language}`}
                                    contentId={contentId}
                                    contentType={contentType}
                                    classId={currentClass?.id ?? null}
                                    sectionOverviews={sortedSectionsOverview}
                                    version={version}
                                    parentElement={null}
                                    language={language}
                                    headerFooterContent={{
                                      unitName: printMetaData.unitName,
                                      subjectName: printMetaData.subjectName,
                                      contentName: printMetaData.contentName,
                                      pageNumber: index + 1,
                                    }}
                                  />
                                )
                              }
                              title={<SimpleCardLabel {...details} />}
                              ratio={ratio}
                              fieldName={PAGES_FIELD}
                              value={page.id}
                              id={page.id}
                              checked={selectedPages.includes(page.id)}
                              onChange={() => {
                                if (selectedPages.includes(page.id)) {
                                  setSelectedPages(
                                    selectedPages.filter(p => p !== page.id)
                                  );
                                } else {
                                  setSelectedPages([...selectedPages, page.id]);
                                }
                              }}
                            />
                          </SimpleModalCardItem>
                        );
                      })}
                    </BoxList>
                    {hasError && (
                      <ErrorMessage>
                        There was an error preparing your file for download.
                        Please try again later.
                      </ErrorMessage>
                    )}
                  </ActivityBuilderThemeContextProvider>
                }
              </>
            )}
          </SimpleModalContentInset>
        </SimpleModalContentInner>
        <SimpleModalActions>
          <Box $gap={16} $alignItems="center">
            <CheckboxInput
              label="Include teacher modelled responses"
              checked={includeResponses}
              disabled={
                !canShowModelledResponses ||
                isGeneratingDownload ||
                isLoadingDownloadPages ||
                isDownloadingAnswers
              }
              onChange={() => setIncludeResponses(!includeResponses)}
            />
          </Box>

          <Box $gap={16} $alignItems="center">
            <SmallStyled marginBottom={0}>
              {`${selectedPages.length} ${pluralize(
                'page',
                selectedPages.length
              )} selected`}
            </SmallStyled>
            <PrimaryButton
              type="button"
              data-selector="modal-download-button"
              loading={
                isDownloadingAnswers ||
                isGeneratingDownload ||
                isLoadingDownloadPages
              }
              disabled={disableDownload}
              onClick={handleDownload}
            >
              Download
            </PrimaryButton>
          </Box>
        </SimpleModalActions>
      </SimpleModalContentOuter>
    </SimpleModal>
  );
};

export default TeacherDownloadActivities;
